import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { get, post, uri } from "helpers/api_helper";
import { GET_ALL_RESOURSEPOOL } from "constants/api_constants";
import { showErrorToast } from "helpers/toastMessages";

const ResourcePool = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  console.log("candidates",candidates);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [uniqueDepartments, setUniqueDepartments] = useState([]);
  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [fileModal, setFileModal] = useState({ open: false, url: "" });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });

  const fetchCandidates = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        pageSize: 10,
        page: 0,
        department: departmentFilter || "",
        searchKey: searchQuery || "",
        location: locationFilter || "",
        status: statusFilter || "",
      };
      const response = await post(GET_ALL_RESOURSEPOOL, payload);
      const { success, message, data } = response;
      if (success && data && Array.isArray(data.data)) {
        const enrichedData = data.data.map((candidate, index) => ({
          candidateId: candidate.candidateId || `fallback-${index}`,
          candidateName: candidate.candidateName || "N/A",
          email: candidate.email || "N/A",
          mobileNumber: candidate.mobileNumber || "N/A",
          source: candidate.source || "N/A",
          experience: candidate.experience || "N/A",
          currentCtc: candidate.currentCtc || "N/A",
          status: candidate.status || "unknown",
          department: candidate.department || "unknown",
          location: candidate.location || "unknown",
          resume: candidate.resume || null,
        }));
        setCandidates(enrichedData);
        setUniqueDepartments([...new Set(enrichedData.map((c) => c.department))]);
        setUniqueLocations([...new Set(enrichedData.map((c) => c.location))]);
        setUniqueStatuses([...new Set(enrichedData.map((c) => c.status))]);
      } else {
        throw new Error(message || "Failed to fetch candidates");
      }
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 405) {
        try {
          const queryParams = new URLSearchParams({
            pageSize: 10,
            page: 0,
            department: departmentFilter || "",
            searchKey: searchQuery || "",
            location: locationFilter || "",
            status: statusFilter || "",
          }).toString();
          const getResponse = await get(`${GET_ALL_RESOURSEPOOL}?${queryParams}`);
          const { success, message, data } = getResponse;
          if (success && data && Array.isArray(data.data)) {
            const enrichedData = data.data.map((candidate, index) => ({
              candidateId: candidate.candidateId || `fallback-${index}`,
              candidateName: candidate.candidateName || "N/A",
              email: candidate.email || "N/A",
              mobileNumber: candidate.mobileNumber || "N/A",
              source: candidate.source || "N/A",
              experience: candidate.experience || "N/A",
              currentCtc: candidate.currentCtc || "N/A",
              status: candidate.status || "unknown",
              department: candidate.department || "unknown",
              location: candidate.location || "unknown",
              resume: candidate.resume || null,
            }));
            setCandidates(enrichedData);
            setUniqueDepartments([...new Set(enrichedData.map((c) => c.department))]);
            setUniqueLocations([...new Set(enrichedData.map((c) => c.location))]);
            setUniqueStatuses([...new Set(enrichedData.map((c) => c.status))]);
            return;
          }
        } catch (getErr) {
          console.error("GET Error:", getErr);
        }
      }
      setError(`Failed to fetch candidates: ${err.message}`);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResume = async (resumeId) => {
    if (!resumeId) {
      showErrorToast("No resume available for this candidate");
      return;
    }

    setApiStatus({ loading: true, error: "" });
    try {
      const response = await get(`${uri}/fileservice/api/files/view/${resumeId}`);
      if (response && response.fileContent) {
        const byteCharacters = atob(response.fileContent);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const fileTypeList = response?.fileName?.split(".");
        const fileType = fileTypeList[fileTypeList.length - 1]?.toLowerCase();
        const mimeTypes = {
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        };
        const mimeType = mimeTypes[fileType] || "application/octet-stream";
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);

        if (fileType === "pdf") {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          setFileModal({ open: true, url });
        }
      } else {
        showErrorToast("Resume file content not found");
      }
    } catch (error) {
      console.error(`Error fetching resume ${resumeId}:`, error);
      showErrorToast(`Failed to fetch resume: ${error.message}`);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleFileModal = () => {
    setFileModal((prev) => ({ ...prev, open: false, url: "" }));
  };

  useEffect(() => {
    fetchCandidates();
  }, [searchQuery, departmentFilter, locationFilter, statusFilter]);

  return (
    <div className="page-content">
      <PageSubHeader
        heading={"Candidate Resource Pool"}
        routePath={"Home"}
        pageName={"Recruitment / Resource Pool"}
      />
      <Container fluid={true} className="py-4 px-4">
        <Row>
          <Row className="px-4 py-4">
            <Col md="3">
              <InputGroup size="sm" className="elbert-search-group">
                <Input
                  placeholder="Search by candidate name"
                  className="elbert-search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
            </Col>
            <Col md="2">
              <Input
                type="select"
                className="elbert-search"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
              >
                <option value="">All Departments</option>
                {uniqueDepartments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept.charAt(0).toUpperCase() + dept.slice(1)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="select"
                className="elbert-search"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="">All Locations</option>
                {uniqueLocations.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc.charAt(0).toUpperCase() + loc.slice(1)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="2">
              <Input
                type="select"
                className="elbert-search"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
          <Row className="mt-4 mx-0 px-4">
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : error ? (
                  <div className="text-center text-warning">{error}</div>
                ) : (
                  <div className="table-responsive">
                    <Table
                      className="table mb-0"
                      style={{ border: "0.4px solid #D4D4D4" }}
                    >
                      <thead>
                        <tr>
                          <th></th>
                          <th style={{ color: "black" }}>Candidate ID</th>
                          <th style={{ color: "black" }}>Name</th>
                          <th style={{ color: "black" }}>Email</th>
                          <th style={{ color: "black" }}>Mobile</th>
                          <th style={{ color: "black" }}>Source</th>
                          <th style={{ color: "black" }}>Exp.</th>
                          <th style={{ color: "black" }}>Current CTC</th>
                          <th style={{ color: "black" }}>Status</th>
                          <th style={{ color: "black" }}>Resume</th>
                          <th style={{ color: "black" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.length > 0 ? (
                          candidates.map((item, index) => (
                            <tr key={item.candidateId || `fallback-${index}`}>
                              <td>
                                <Input
                                  type="checkbox"
                                  style={{ width: "17px", height: "17px" }}
                                />
                              </td>
                              <td>{item.candidateId}</td>
                              <td>{item.candidateName}</td>
                              <td>{item.email}</td>
                              <td>{item.mobileNumber}</td>
                              <td>{item.source}</td>
                              <td>{item.experience}</td>
                              <td>{item.currentCtc}</td>
                              <td
                                style={{
                                  color:
                                    item.status === "In-progress"
                                      ? "#006438"
                                      : item.status === "Put on hold"
                                      ? "#FFA500"
                                      : "#EB7979",
                                }}
                              >
                                {item.status}
                              </td>
                              <td>
                                {item.resume ? (
                                  <i
                                    className="fa-solid fa-file-pdf"
                                    title="View Resume"
                                    style={{
                                      color: "#FF0000",
                                      fontSize: "19px",
                                      marginLeft: "5px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() => handleViewResume(item.resume)}
                                  />
                                ) : (
                                  <i
                                    className="fa-solid fa-file-pdf"
                                    title="No Resume"
                                    style={{
                                      color: "#D3D3D3",
                                      fontSize: "19px",
                                      marginLeft: "5px",
                                    }}
                                  />
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2 align-items-center align-content-center">
                                  <i
                                    className="fa-solid fa-eye action-icon"
                                    title="View"
                                    style={{ cursor: "pointer", marginLeft: "5px" }}
                                    onClick={() => navigate(`/view-candidate-profile/${item.id}`)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="11" className="text-center">
                              No candidates found for the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Row>
        </Row>
        <Modal
          isOpen={fileModal.open}
          toggle={handleFileModal}
          centered
          size="lg"
          backdrop="static"
        >
          <ModalHeader toggle={handleFileModal}>View Resume</ModalHeader>
          <ModalBody>
            {fileModal.url ? (
              <iframe
                src={fileModal.url}
                style={{ width: "100%", height: "500px" }}
                title="Resume"
              />
            ) : (
              <div className="text-center">No file available</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleFileModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ResourcePool;